import { Megaphone, type LucideIcon } from 'lucide-react';
import { EVENT_ICONS } from '@/shared/config';

export type NotificationTone = 'neutral' | 'info' | 'positive' | 'warning' | 'danger';

interface EventVisual {
  Icon: LucideIcon;
  tone: NotificationTone;
  label: string;
}

const EVENT_VISUALS: Record<string, Omit<EventVisual, 'Icon'>> = {
  ticket_created: { tone: 'info', label: 'Создана заявка' },
  ticket_created_vip: { tone: 'warning', label: 'Заявка VIP-заявителя' },
  message_sent: { tone: 'info', label: 'Новое сообщение' },
  mentioned: { tone: 'warning', label: 'Вас упомянули' },
  status_changed: { tone: 'info', label: 'Смена статуса' },
  participant_added: { tone: 'neutral', label: 'Добавлен участник' },
  participant_removed: { tone: 'neutral', label: 'Участник удалён' },
  assignee_added: { tone: 'positive', label: 'Назначен исполнитель' },
  assignee_removed: { tone: 'neutral', label: 'Исполнитель снят' },
  assignee_reassigned: { tone: 'info', label: 'Переназначение' },
  approval_created: { tone: 'warning', label: 'Запрошено согласование' },
  approval_approved: { tone: 'positive', label: 'Согласовано' },
  approval_rejected: { tone: 'danger', label: 'Отказано в согласовании' },
  approval_round_approved: { tone: 'positive', label: 'Круг согласован' },
  approval_round_rejected: { tone: 'danger', label: 'Круг отклонён' },
  approval_level_reminded: { tone: 'warning', label: 'Напоминание о согласовании' },
  folder_grant_expired: { tone: 'warning', label: 'Истёк срок доступа' },
  task_assigned: { tone: 'positive', label: 'Назначена задача' },
  task_reopened: { tone: 'warning', label: 'Задача снова в работе' },
  task_ticket_finished: { tone: 'info', label: 'Заявка задачи закрыта' },
  task_commented: { tone: 'info', label: 'Комментарий в задаче' },
  task_mentioned: { tone: 'warning', label: 'Вас упомянули в задаче' },
  sprint_started: { tone: 'info', label: 'Спринт стартовал' },
  sprint_closed: { tone: 'positive', label: 'Спринт закрыт' },
  sprint_overdue: { tone: 'warning', label: 'Спринт просрочен' },
  chat_mentioned: { tone: 'warning', label: 'Вас упомянули в чате группы' },
  chat_message: { tone: 'info', label: 'Сообщение в канале' },
  system_status: { tone: 'danger', label: 'Сбой портала' },
  system_status_recovered: { tone: 'positive', label: 'Портал восстановлен' },
};

const FALLBACK: EventVisual = { Icon: Megaphone, tone: 'neutral', label: 'Событие по заявке' };

const ICON_KEY_ALIAS: Partial<Record<string, string>> = {
  task_ticket_finished: 'ticket_finished',
  system_status_recovered: 'system_status',
};

function resolveEventType(eventType: string, payload?: Record<string, unknown> | null): string {
  if (eventType === 'task_reopened' && payload?.reason === 'ticket_finished') return 'task_ticket_finished';
  if (eventType === 'system_status' && payload?.status === 'up') return 'system_status_recovered';
  if (eventType === 'ticket_created' && payload?.vip === true) return 'ticket_created_vip';
  return eventType;
}

export function notificationVisual(eventType: string, payload?: Record<string, unknown> | null): EventVisual {
  const resolved = resolveEventType(eventType, payload);
  const visual = EVENT_VISUALS[resolved];
  if (!visual) return FALLBACK;
  return { Icon: EVENT_ICONS[ICON_KEY_ALIAS[resolved] ?? resolved] ?? FALLBACK.Icon, ...visual };
}

export function NotificationEventIcon({
  eventType,
  payload,
  size = 14,
}: {
  eventType: string;
  payload?: Record<string, unknown> | null;
  size?: number;
}): React.ReactElement {
  const { Icon } = notificationVisual(eventType, payload);
  return <Icon size={size} aria-hidden />;
}
